import { cloneSpec, FLEET } from "./airports";
import { hydrateMxBases } from "./mxBases";
import { hydrateTombstoneRecords } from "./rowRevision";
import { crewIdFromName } from "./duty";
import { newCrewMember } from "./crew";
import { DEFAULT_FTL_SCHEME, hydrateDutyTablePresetId, hydrateFtlScheme } from "./ftlScheme";
import {
  DEFAULT_PILOT,
  FICTION_HOME_ICAO,
  HAWKER_ICAO_TYPE,
  OPERATOR_NAME,
  PC12_ICAO_TYPE,
  SUGGESTED_COMPANY_NAME,
  SUGGESTED_HOME_ICAO,
  normalizeRatedTypes,
} from "./profile";
import { DEFAULT_FUEL_UNIT, hydrateFuelUnit } from "./fuelUnits";
import {
  DEFAULT_THEME_ACCENT,
  DEFAULT_THEME_PACK,
  hydrateThemeAccent,
  hydrateThemeId,
  hydrateThemePack,
} from "./themePacks";
import {
  DEFAULT_FLIGHT_NUMBER_SCHEME,
  FIRST_FLIGHT_SEQ,
  hydrateFerryNumberMode,
  hydrateFlightNumberDigits,
  hydrateFlightNumberScheme,
  hydrateFlightStart,
  hydrateNextFlightSeq,
  normalizeFlightPrefix,
} from "./flightNumber";
import {
  DEFAULT_CHARTER_OPS_BRIEF,
  DEFAULT_CHARTER_OPS_BRIEF_TITLE,
  hydrateCharterOpsBrief,
  hydrateCharterOpsBriefTitle,
} from "./charterOpsBrief";
import {
  DEFAULT_RECENT_ROUTE_COOLDOWN_DAYS,
  hydrateInboxRouteMemory,
  hydrateRecentRouteSettings,
} from "./inboxMintMemory";
import { DEFAULT_DESK_BEHAVIOR, hydrateDeskBehavior } from "./deskBehavior";
import { defaultWidgetLayouts, hydrateWidgetLayouts } from "./widgetBoard";
import { defaultOpsDeskSettings, hydrateOpsDeskSettings } from "./opsDeskSettings";
import type { AircraftSpec, CompanyKind, CompanyProfile, CrewMember, DeskRole } from "./types";

/** Catalog types the first-run wizard / Settings fleet picker can offer. */
export const FLEET_TYPE_CATALOG: { icaoType: string; name: string; seedId: string }[] = [
  { icaoType: HAWKER_ICAO_TYPE, name: "Hawker 800XP (no winglets)", seedId: "hawker-800xp" },
  { icaoType: PC12_ICAO_TYPE, name: "Pilatus PC-12 NG", seedId: "pc12-ng" },
];

export function newCompanyId(now = new Date()): string {
  return `co-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

/** Eight uppercase chars, no ambiguous 0/O/1/I. */
export function newInviteCode(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 8; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

export function normalizeIcaoCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
}

export function normalizeCompanyKind(raw?: string | null): CompanyKind {
  if (raw === "air_taxi" || raw === "corporate" || raw === "owner" || raw === "charter") return raw;
  return "charter";
}

export function normalizeInviteCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** Display form `ABCD-EFGH`. Hyphens are optional when joining. */
export function formatInviteCode(raw: string): string {
  const code = normalizeInviteCode(raw);
  if (!code) return "";
  if (code.length <= 4) return code;
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

export type CrewInvitePacket = {
  companyName: string;
  inviteCode: string;
  syncUrl?: string;
  /** True when a token is configured — never put the token in the packet. */
  tokenConfigured?: boolean;
};

/**
 * Copy-paste card for a second desk. Token stays in Settings → Data / Sync;
 * the packet only says whether one is required.
 */
export function crewInvitePacket(input: CrewInvitePacket): string {
  const name = input.companyName.trim() || "Company";
  const code = formatInviteCode(input.inviteCode) || "(rotate an invite code in Settings → Crew)";
  const url = input.syncUrl?.trim() || "(set Sync server URL in Settings → Data / Sync, then Push)";
  const tokenLine = input.tokenConfigured
    ? "Sync API token: same token as Settings → Data / Sync on the host desk (not printed here)."
    : "Sync API token: required to Pull the shared board after join. Ask the host for the token from Settings → Data / Sync.";
  return [
    `Join ${name} on Northstar Dispatch`,
    `Invite code: ${code}`,
    `Sync URL: ${url}`,
    tokenLine,
    "On the other PC: first-run → Join company → paste code + URL + token → Look up company → Join. The desk Pulls fleet, board, Inbox, ledger, and crew after join. Auto-inbox RFQs mint on the host desk only. Labels stay Company and Crew.",
  ].join("\n");
}

/** ICAO type designator — letters/digits only, so `FA-50` becomes `FA50`. Up to 8 so HS25B fits. */
export function normalizeIcaoTypeDesignator(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
}

export function normalizeOperatedTypes(types?: string[]): string[] {
  const list = (types ?? []).map((t) => normalizeIcaoTypeDesignator(t)).filter(Boolean);
  return [...new Set(list)];
}

/** Types actually on the Fleet line — source of truth for “what we can fly.” */
export function fleetIcaoTypes(fleet: AircraftSpec[]): string[] {
  return normalizeOperatedTypes(fleet.map((a) => a.icaoType));
}

/**
 * Union Settings `operatedIcaoTypes` with every type on Fleet so the allow-list
 * cannot silently drop a tail that is already on the line.
 */
export function syncOperatedTypesFromFleet(
  company: CompanyProfile,
  fleet: AircraftSpec[],
): CompanyProfile {
  const next = normalizeOperatedTypes([...company.operatedIcaoTypes, ...fleet.map((a) => a.icaoType)]);
  const current = normalizeOperatedTypes(company.operatedIcaoTypes);
  if (next.length === current.length && next.every((t, i) => t === current[i])) return company;
  return { ...company, operatedIcaoTypes: next };
}

export type OperatedTypeChoice = {
  icaoType: string;
  name: string;
  catalog: boolean;
  onLine: boolean;
};

/** Catalog types plus any extra types already on Fleet / operated list. */
export function operatedTypeChoices(
  company: Pick<CompanyProfile, "operatedIcaoTypes">,
  fleet: AircraftSpec[] = [],
): OperatedTypeChoice[] {
  const onLine = new Set(fleetIcaoTypes(fleet));
  const extra = normalizeOperatedTypes([...company.operatedIcaoTypes, ...fleet.map((a) => a.icaoType)]);
  const seen = new Set<string>();
  const out: OperatedTypeChoice[] = [];
  for (const icaoType of extra) {
    if (seen.has(icaoType)) continue;
    seen.add(icaoType);
    const named = fleet.find((a) => normalizeIcaoTypeDesignator(a.icaoType) === icaoType);
    out.push({
      icaoType,
      name: named?.name?.trim() || catalogTypeLabel(icaoType),
      catalog: FLEET_TYPE_CATALOG.some((row) => row.icaoType === icaoType),
      onLine: onLine.has(icaoType),
    });
  }
  return out;
}

function hasSetupFlag(raw?: Partial<CompanyProfile>): boolean {
  return Boolean(raw && typeof raw.setupComplete === "boolean");
}

function flightNumberingFromRaw(
  raw: Partial<CompanyProfile> | null | undefined,
  setupComplete: boolean,
): Pick<
  CompanyProfile,
  | "flightNumberPrefix"
  | "flightNumberStart"
  | "flightNumberDigits"
  | "ferryNumberMode"
  | "ferryStartSeq"
  | "nextFerrySeq"
> {
  const scheme = hydrateFlightNumberScheme(raw, { setupComplete });
  const nextFerry = hydrateFlightStart(raw?.nextFerrySeq, scheme.ferryStartSeq);
  return {
    flightNumberPrefix: scheme.prefix,
    flightNumberStart: scheme.startSeq,
    flightNumberDigits: scheme.digitWidth,
    ferryNumberMode: scheme.ferryMode,
    ferryStartSeq: scheme.ferryStartSeq,
    nextFerrySeq: Math.max(scheme.ferryStartSeq, nextFerry),
  };
}

export type HydrateCompanyOptions = {
  /**
   * Older persisted desks (pre–Company setup) already have an operator — treat
   * them as configured so the wizard does not fire again.
   */
  legacyPersist?: boolean;
};

export function hydrateCompany(
  raw?: Partial<CompanyProfile> | null,
  opts: HydrateCompanyOptions = {},
): CompanyProfile {
  const setupComplete =
    raw?.setupComplete === true ||
    (opts.legacyPersist === true && raw?.setupComplete !== false && Boolean(raw?.operatorName || raw?.homeIcao));
  const { homeIcao, extraBases, bases } = hydrateCompanyBases(raw);
  const hasOperatedField = Boolean(raw && "operatedIcaoTypes" in raw);
  const operated = normalizeOperatedTypes(raw?.operatedIcaoTypes);
  const operatedIcaoTypes = operated.length
    ? operated
    : hasOperatedField
      ? []
      : setupComplete || opts.legacyPersist
        ? FLEET_TYPE_CATALOG.map((t) => t.icaoType)
        : [];
  return {
    id: raw?.id?.trim() || (setupComplete ? newCompanyId() : ""),
    operatorName: raw?.operatorName?.trim() || (setupComplete ? OPERATOR_NAME : SUGGESTED_COMPANY_NAME),
    homeIcao,
    extraBases,
    bases,
    enforceDutyClock: raw?.enforceDutyClock !== false,
    ftlScheme: hydrateFtlScheme(raw?.ftlScheme),
    dutyTablePresetId: hydrateDutyTablePresetId(raw?.dutyTablePresetId, hydrateFtlScheme(raw?.ftlScheme)),
    operatedIcaoTypes,
    setupComplete,
    inviteCode: normalizeInviteCode(raw?.inviteCode ?? "") || (setupComplete ? newInviteCode() : ""),
    createdAtUtc: raw?.createdAtUtc || (setupComplete ? new Date().toISOString() : ""),
    companyKind: normalizeCompanyKind(raw?.companyKind),
    useTelephonyCallsign: Boolean(raw?.useTelephonyCallsign),
    telephonyName: raw?.telephonyName?.trim() ?? "",
    ...flightNumberingFromRaw(raw, setupComplete),
    nextFlightSeq: hydrateNextFlightSeq(
      raw?.nextFlightSeq,
      [],
      hydrateFlightNumberScheme(raw, { setupComplete }).startSeq,
    ),
    defaultFuelUnit: hydrateFuelUnit(raw?.defaultFuelUnit, DEFAULT_FUEL_UNIT),
    theme: hydrateThemeId(raw?.theme),
    themePack: hydrateThemePack(raw?.themePack),
    themeAccent: hydrateThemeAccent(raw?.themeAccent),
    contentXmlPath: typeof raw?.contentXmlPath === "string" ? raw.contentXmlPath.trim() : "",
    techStopIcaos: normalizeTechStopIcaos(raw?.techStopIcaos),
    scheduleOnlyContentXmlAirports: raw?.scheduleOnlyContentXmlAirports === true,
    allowPilotsCompanySchedule: raw?.allowPilotsCompanySchedule === true,
    charterOpsBriefTitle: hydrateCharterOpsBriefTitle(raw?.charterOpsBriefTitle),
    charterOpsBrief: hydrateCharterOpsBrief(raw?.charterOpsBrief),
    deprioritizeRecentRoutes: hydrateRecentRouteSettings(raw).deprioritizeRecentRoutes,
    recentRouteCooldownDays: hydrateRecentRouteSettings(raw).recentRouteCooldownDays,
    inboxRouteMemory: hydrateInboxRouteMemory(raw?.inboxRouteMemory),
    removedCrewIds: hydrateRemovedCrewIds(raw?.removedCrewIds),
    removedInboxIds: hydrateRemovedCrewIds(raw?.removedInboxIds),
    removedMissionIds: hydrateRemovedCrewIds(raw?.removedMissionIds),
    removedFlightLogIds: hydrateRemovedCrewIds(raw?.removedFlightLogIds),
    boardTombstoneRecords: hydrateTombstoneRecords(raw?.boardTombstoneRecords),
    mxBases: hydrateMxBases(raw?.mxBases),
    activeFlightId: typeof raw?.activeFlightId === "string" ? raw.activeFlightId.trim() : "",
    activeFlightAuthority:
      raw?.activeFlightAuthority === "desk" || raw?.activeFlightAuthority === "megan" || raw?.activeFlightAuthority === "sim"
        ? raw.activeFlightAuthority
        : undefined,
    activeFlightAt: typeof raw?.activeFlightAt === "string" ? raw.activeFlightAt : "",
    activeFlightClearedAt: typeof raw?.activeFlightClearedAt === "string" ? raw.activeFlightClearedAt : "",
    layoutSignOffMilestone: typeof raw?.layoutSignOffMilestone === "string" ? raw.layoutSignOffMilestone.trim() : "",
    ganttTimeDisplay: raw?.ganttTimeDisplay === "zulu" ? "zulu" : "local",
    deskBehavior: hydrateDeskBehavior(raw?.deskBehavior),
    widgetLayouts: hydrateWidgetLayouts(raw?.widgetLayouts),
    opsDesk: hydrateOpsDeskSettings(raw?.opsDesk),
  };
}

/** Deduped crew ids that left or were removed. Empty on older companies. */
export function hydrateRemovedCrewIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const id = typeof row === "string" ? row.trim() : "";
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function unionRemovedCrewIds(...lists: unknown[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const id of hydrateRemovedCrewIds(list)) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

function normalizeTechStopIcaos(raw: unknown): string[] {
  const parts = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(/[^A-Za-z0-9]+/) : [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const icao = normalizeIcaoCode(String(part ?? ""));
    if (icao.length < 3 || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
  }
  return out;
}

/** Fresh install — wizard required. Suggested name is editable, not locked. */
export const DEFAULT_COMPANY: CompanyProfile = {
  id: "",
  operatorName: SUGGESTED_COMPANY_NAME,
  homeIcao: SUGGESTED_HOME_ICAO,
  extraBases: [],
    bases: SUGGESTED_HOME_ICAO ? [SUGGESTED_HOME_ICAO] : [],
    mxBases: [],
    enforceDutyClock: true,
  ftlScheme: DEFAULT_FTL_SCHEME,
  dutyTablePresetId: "car704",
  operatedIcaoTypes: [],
  setupComplete: false,
  inviteCode: "",
  createdAtUtc: "",
  companyKind: "charter",
    useTelephonyCallsign: false,
    telephonyName: "",
    flightNumberPrefix: DEFAULT_FLIGHT_NUMBER_SCHEME.prefix,
    flightNumberStart: DEFAULT_FLIGHT_NUMBER_SCHEME.startSeq,
    flightNumberDigits: DEFAULT_FLIGHT_NUMBER_SCHEME.digitWidth,
    ferryNumberMode: DEFAULT_FLIGHT_NUMBER_SCHEME.ferryMode,
    ferryStartSeq: DEFAULT_FLIGHT_NUMBER_SCHEME.ferryStartSeq,
    nextFerrySeq: DEFAULT_FLIGHT_NUMBER_SCHEME.ferryStartSeq,
    nextFlightSeq: FIRST_FLIGHT_SEQ,
    defaultFuelUnit: DEFAULT_FUEL_UNIT,
    theme: "light",
    themePack: DEFAULT_THEME_PACK,
    themeAccent: DEFAULT_THEME_ACCENT,
    contentXmlPath: "",
    techStopIcaos: [],
    scheduleOnlyContentXmlAirports: false,
    allowPilotsCompanySchedule: false,
    charterOpsBriefTitle: DEFAULT_CHARTER_OPS_BRIEF_TITLE,
    charterOpsBrief: DEFAULT_CHARTER_OPS_BRIEF,
    deprioritizeRecentRoutes: true,
    recentRouteCooldownDays: DEFAULT_RECENT_ROUTE_COOLDOWN_DAYS,
    inboxRouteMemory: [],
    removedCrewIds: [],
    removedInboxIds: [],
    removedMissionIds: [],
    removedFlightLogIds: [],
    boardTombstoneRecords: [],
    activeFlightId: "",
    activeFlightAt: "",
    activeFlightClearedAt: "",
    layoutSignOffMilestone: "",
    ganttTimeDisplay: "local",
    deskBehavior: { ...DEFAULT_DESK_BEHAVIOR, releaseCriticalItems: [...DEFAULT_DESK_BEHAVIOR.releaseCriticalItems] },
    widgetLayouts: defaultWidgetLayouts(),
    opsDesk: defaultOpsDeskSettings(),
  };

/** Normalize a company bases list. Empty / missing list seeds from `homeIcao` only — never CYPD. */
export function hydrateCompanyBases(
  raw?: Pick<CompanyProfile, "homeIcao" | "extraBases" | "bases"> | Partial<CompanyProfile> | null,
): { homeIcao: string; extraBases: string[]; bases: string[] } {
  const listed = (raw?.bases ?? [])
    .map((icao) => normalizeIcaoCode(String(icao ?? "")))
    .filter((icao) => icao.length >= 3);
  const extrasRaw = (raw?.extraBases ?? [])
    .map((icao) => normalizeIcaoCode(String(icao ?? "")))
    .filter((icao) => icao.length >= 3);
  const homeRaw = normalizeIcaoCode(raw?.homeIcao ?? "");
  const hasHomeOrExtra = Boolean(raw && ("homeIcao" in raw || "extraBases" in raw));
  if (!hasHomeOrExtra && listed.length) {
    const homeIcao = listed[0]!;
    const extraBases = [...new Set(listed.slice(1).filter((icao) => icao !== homeIcao))];
    return { homeIcao, extraBases, bases: [homeIcao, ...extraBases] };
  }
  const homeIcao = homeRaw || listed[0] || SUGGESTED_HOME_ICAO || "";
  const extraBases = [...new Set(extrasRaw.filter((icao) => icao && icao !== homeIcao))];
  return { homeIcao, extraBases, bases: homeIcao ? [homeIcao, ...extraBases] : extraBases };
}

export function companyHomeBases(
  company: Pick<CompanyProfile, "homeIcao" | "extraBases" | "bases"> | CompanyProfile,
): string[] {
  return hydrateCompanyBases(company).bases;
}

function withCompanyBases(company: CompanyProfile, homeIcao: string, extraBases: string[]): CompanyProfile {
  const extra = [...new Set(extraBases.map(normalizeIcaoCode).filter((icao) => icao && icao !== homeIcao))];
  return { ...company, homeIcao, extraBases: extra, bases: homeIcao ? [homeIcao, ...extra] : extra };
}

export function withPrimaryBase(company: CompanyProfile, icao: string): CompanyProfile {
  const next = normalizeIcaoCode(icao);
  if (next.length < 3) return company;
  const extras = companyHomeBases(company).filter((b) => b !== next);
  return withCompanyBases(company, next, extras);
}

export function withAddedBase(company: CompanyProfile, icao: string): CompanyProfile {
  const next = normalizeIcaoCode(icao);
  if (next.length < 3) return company;
  if (companyHomeBases(company).includes(next)) return company;
  const home = normalizeIcaoCode(company.homeIcao);
  if (!home) return withCompanyBases(company, next, []);
  return withCompanyBases(company, home, [...company.extraBases, next]);
}

export function withRemovedBase(company: CompanyProfile, icao: string): CompanyProfile {
  const code = normalizeIcaoCode(icao);
  const home = normalizeIcaoCode(company.homeIcao);
  if (code === home) return company;
  return withCompanyBases(
    company,
    home,
    company.extraBases.filter((b) => normalizeIcaoCode(b) !== code),
  );
}

/** Assigned airframe base, else company primary (seeded from homeIcao). */
export function airframeBaseIcao(
  spec?: Pick<AircraftSpec, "baseIcao"> | null,
  company?: Partial<Pick<CompanyProfile, "homeIcao" | "extraBases" | "bases">> | null,
): string {
  const assigned = normalizeIcaoCode(spec?.baseIcao ?? "");
  if (assigned.length >= 3) return assigned;
  return hydrateCompanyBases(company).bases[0] ?? "";
}

/** Company primary + extras + optional tail base. Sole mint / Create origin allow-list. */
export function companyOriginIcaos(
  company?: Partial<Pick<CompanyProfile, "homeIcao" | "extraBases" | "bases">> | null,
  spec?: Pick<AircraftSpec, "baseIcao"> | null,
): string[] {
  const assigned = normalizeIcaoCode(spec?.baseIcao ?? "");
  const { homeIcao, extraBases } = hydrateCompanyBases(company);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of [assigned, homeIcao, ...extraBases]) {
    const icao = normalizeIcaoCode(raw);
    if (icao.length < 3 || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
  }
  return out;
}

/**
 * Catalog tails ship with Emerson’s CYPD fiction base. After a non-CYPD company
 * is created, leftover CYPD bases that are not on the company list rebase home.
 * Celtic (home or listed CYPD) saves stay put.
 */
export function alignFleetOriginsToCompany(
  fleet: AircraftSpec[],
  company?: Partial<Pick<CompanyProfile, "homeIcao" | "extraBases" | "bases">> | null,
): AircraftSpec[] {
  const home = hydrateCompanyBases(company).homeIcao;
  const bases = new Set(companyHomeBases({ homeIcao: home, extraBases: company?.extraBases ?? [], bases: company?.bases }));
  if (!home || home === FICTION_HOME_ICAO || bases.has(FICTION_HOME_ICAO)) return fleet;
  return fleet.map((spec) => {
    const base = normalizeIcaoCode(spec.baseIcao ?? "");
    const last = normalizeIcaoCode(spec.lastAirportIcao ?? "");
    const catalogBase = !base || base === FICTION_HOME_ICAO;
    if (!catalogBase) return spec;
    return {
      ...spec,
      baseIcao: home,
      lastAirportIcao: !last || last === FICTION_HOME_ICAO ? home : last,
    };
  });
}

export function rotateInviteCode(company: CompanyProfile): CompanyProfile {
  return { ...company, inviteCode: newInviteCode() };
}

export function inviteMatches(company: CompanyProfile, code: string): boolean {
  const expected = normalizeInviteCode(company.inviteCode);
  const got = normalizeInviteCode(code);
  return Boolean(expected && got && expected === got);
}

/**
 * Fleet list is source of truth for mint / Classic / assign.
 * Settings “company fleet types” may seed catalog tails; it does not hide an
 * airframe that is already on Manage → Fleet.
 */
export function filterFleetByCompany(fleet: AircraftSpec[], _company?: CompanyProfile): AircraftSpec[] {
  return fleet;
}

export function seedFleetForTypes(types: string[]): AircraftSpec[] {
  const want = normalizeOperatedTypes(types);
  const picked = FLEET.filter((a) => want.includes(a.icaoType.toUpperCase())).map(cloneSpec);
  return picked.length ? picked : FLEET.map(cloneSpec);
}

export function catalogTypeLabel(icaoType: string): string {
  const hit = FLEET_TYPE_CATALOG.find((t) => t.icaoType === icaoType.toUpperCase());
  return hit ? `${hit.icaoType} — ${hit.name}` : icaoType.toUpperCase();
}

export function knownBaseOptions(destinations: { icao: string; name: string }[]): { icao: string; name: string }[] {
  const seen = new Set<string>();
  const out: { icao: string; name: string }[] = [];
  for (const row of destinations) {
    const icao = normalizeIcaoCode(row.icao);
    if (!icao || seen.has(icao)) continue;
    seen.add(icao);
    out.push({ icao, name: row.name });
  }
  return out;
}

export type CompanySetupInput = {
  operatorName: string;
  homeIcao: string;
  extraBases?: string[];
  operatedIcaoTypes: string[];
  adminName: string;
  adminRoles?: DeskRole[];
  now?: Date;
  /** Optional hashed desk PIN for the first admin (UI hashes before calling). */
  pinSalt?: string;
  pinHash?: string;
  companyKind?: CompanyKind;
  useTelephonyCallsign?: boolean;
  telephonyName?: string;
  flightNumberPrefix?: string;
  flightNumberStart?: number;
  flightNumberDigits?: 3 | 4;
  ferryNumberMode?: CompanyProfile["ferryNumberMode"];
  ferryStartSeq?: number;
  /** Tails the user typed. Omitted means none — catalog presets are not copied. */
  fleet?: AircraftSpec[];
  /** Users the user added. Omitted means the single admin fields below. */
  crew?: Array<{
    displayName: string;
    roles: DeskRole[];
    isAdmin: boolean;
    ratedIcaoTypes: string[];
    pinSalt: string;
    pinHash: string;
  }>;
  ratedIcaoTypes?: string[];
};

export type CompanySetupResult = {
  company: CompanyProfile;
  admin: CrewMember;
  crew: CrewMember[];
  fleet: AircraftSpec[];
};

export function completeCompanySetup(input: CompanySetupInput): CompanySetupResult {
  const now = input.now ?? new Date();
  const name = input.operatorName.trim() || SUGGESTED_COMPANY_NAME;
  const home = normalizeIcaoCode(input.homeIcao) || SUGGESTED_HOME_ICAO || "";
  const providedFleet = input.fleet ?? [];
  const types = normalizeOperatedTypes([
    ...input.operatedIcaoTypes,
    ...providedFleet.map((row) => row.icaoType),
  ]);
  const operated = types;
  const extra = (input.extraBases ?? [])
    .map(normalizeIcaoCode)
    .filter((icao) => icao && icao !== home);
  const company: CompanyProfile = {
    id: newCompanyId(now),
    operatorName: name,
    homeIcao: home,
    extraBases: [...new Set(extra)],
    bases: home ? [home, ...new Set(extra)] : [...new Set(extra)],
    mxBases: [],
    enforceDutyClock: true,
    ftlScheme: DEFAULT_FTL_SCHEME,
    dutyTablePresetId: "car704",
    operatedIcaoTypes: operated,
    setupComplete: true,
    inviteCode: newInviteCode(),
    createdAtUtc: now.toISOString(),
    companyKind: normalizeCompanyKind(input.companyKind),
    useTelephonyCallsign: Boolean(input.useTelephonyCallsign),
    telephonyName: input.telephonyName?.trim() ?? "",
    flightNumberPrefix: normalizeFlightPrefix(input.flightNumberPrefix ?? ""),
    flightNumberStart: hydrateFlightStart(input.flightNumberStart),
    flightNumberDigits: hydrateFlightNumberDigits(input.flightNumberDigits),
    ferryNumberMode: hydrateFerryNumberMode(input.ferryNumberMode),
    ferryStartSeq: hydrateFlightStart(input.ferryStartSeq, DEFAULT_FLIGHT_NUMBER_SCHEME.ferryStartSeq),
    nextFerrySeq: hydrateFlightStart(input.ferryStartSeq, DEFAULT_FLIGHT_NUMBER_SCHEME.ferryStartSeq),
    nextFlightSeq: hydrateFlightStart(input.flightNumberStart),
    defaultFuelUnit: DEFAULT_FUEL_UNIT,
    theme: "light",
    themePack: DEFAULT_THEME_PACK,
    themeAccent: DEFAULT_THEME_ACCENT,
    contentXmlPath: "",
    techStopIcaos: [],
    scheduleOnlyContentXmlAirports: false,
    allowPilotsCompanySchedule: false,
    charterOpsBriefTitle: DEFAULT_CHARTER_OPS_BRIEF_TITLE,
    charterOpsBrief: DEFAULT_CHARTER_OPS_BRIEF,
    deprioritizeRecentRoutes: true,
    recentRouteCooldownDays: DEFAULT_RECENT_ROUTE_COOLDOWN_DAYS,
    inboxRouteMemory: [],
    removedCrewIds: [],
    removedInboxIds: [],
    removedMissionIds: [],
    removedFlightLogIds: [],
    boardTombstoneRecords: [],
    activeFlightId: "",
    activeFlightAt: "",
    activeFlightClearedAt: "",
    layoutSignOffMilestone: "",
    ganttTimeDisplay: "local",
    deskBehavior: { ...DEFAULT_DESK_BEHAVIOR, releaseCriticalItems: [...DEFAULT_DESK_BEHAVIOR.releaseCriticalItems] },
    widgetLayouts: defaultWidgetLayouts(),
    opsDesk: defaultOpsDeskSettings(),
  };
  const adminName = input.adminName.trim() || DEFAULT_PILOT.name;
  const roles = input.adminRoles?.length ? input.adminRoles : (["dispatcher", "pilot"] as DeskRole[]);
  const admin: CrewMember = {
    id: crewIdFromName(adminName),
    displayName: adminName,
    roles: [...new Set(roles)],
    isAdmin: true,
    communityFolderPath: "",
    contentXmlPath: "",
    sceneryInventory: [],
    sceneryGateEnabled: true,
    pilotScheduleView: "mine",
    ratedIcaoTypes: normalizeOperatedTypes(
      input.ratedIcaoTypes ??
        (operated.includes(HAWKER_ICAO_TYPE) ? [HAWKER_ICAO_TYPE] : operated.slice(0, 1)),
    ),
    createdAtUtc: now.toISOString(),
    pinSalt: input.pinSalt?.trim() ?? "",
    pinHash: input.pinHash?.trim() ?? "",
    phone: "",
    routeAreaPrefs: "",
    preferredTripNm: null,
  };
  const seeded =
    input.fleet == null
      ? seedFleetForTypes(operated.length ? operated : FLEET_TYPE_CATALOG.map((row) => row.icaoType))
      : providedFleet;
  const fleet = seeded.map((spec) => ({
    ...spec,
    baseIcao: home || normalizeIcaoCode(spec.baseIcao ?? "") || spec.baseIcao,
    lastAirportIcao: home || normalizeIcaoCode(spec.lastAirportIcao ?? "") || spec.lastAirportIcao,
  }));
  const crew = (input.crew?.length
    ? input.crew
    : [
        {
          displayName: admin.displayName,
          roles: admin.roles,
          isAdmin: true,
          ratedIcaoTypes: admin.ratedIcaoTypes,
          pinSalt: admin.pinSalt,
          pinHash: admin.pinHash,
        },
      ]
  ).map((member) => {
    const built = newCrewMember({
      displayName: member.displayName,
      roles: member.roles,
      isAdmin: member.isAdmin,
      ratedIcaoTypes: member.ratedIcaoTypes,
      now,
      id: crewIdFromName(member.displayName),
    });
    built.ratedIcaoTypes = normalizeOperatedTypes(member.ratedIcaoTypes);
    built.pinSalt = member.pinSalt;
    built.pinHash = member.pinHash;
    return built;
  });
  const lead = crew.find((member) => member.isAdmin) ?? crew[0] ?? admin;
  return {
    company,
    admin: lead,
    crew,
    fleet,
  };
}

export function companyConfigured(company: CompanyProfile | undefined): boolean {
  return Boolean(company?.setupComplete && company.operatorName.trim());
}

export { hasSetupFlag };
